import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCoverages,
  getProfessionalCoverages,
  updateProfessionalCoverages,
} from '../services/api';
import './CoveragesModal.css';

// NER-1778 (FE): coberturas que acepta un profesional. El master define el set de
// coberturas posibles; por profesional se elige cuáles acepta y, opcionalmente, un
// `priceOverride` (precio final absoluto) que pisa el ajuste default de la coverage.
// PUT /professionals/{id}/coverages reemplaza el set aceptado del profesional.
const DEFAULT_LABEL = 'Cobertura';

function CoveragesModal({ professional, onClose }) {
  const { t } = useTranslation();

  const [master, setMaster] = useState([]);
  const [coverageLabel, setCoverageLabel] = useState('');
  // rows: { coverageKey, accepted, priceOverride: string ('' = hereda default) }
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const label = coverageLabel || DEFAULT_LABEL;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [covRes, profRes] = await Promise.all([
        getCoverages(),
        getProfessionalCoverages(professional.professionalId),
      ]);
      const masterCoverages = covRes?.master?.coverages || [];
      setMaster(masterCoverages);
      setCoverageLabel(covRes?.master?.coverageLabel || '');
      const accepted = profRes?.acceptedCoverages || [];
      setRows(
        masterCoverages.map((c) => {
          const entry = accepted.find((a) => a.coverageKey === c.key);
          return {
            coverageKey: c.key,
            accepted: !!entry,
            priceOverride:
              entry && entry.priceOverride != null ? String(entry.priceOverride) : '',
          };
        })
      );
    } catch (err) {
      console.error('Error loading professional coverages:', err);
      setError(t('professionalCoverages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [professional.professionalId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAccept = (coverageKey) => {
    setRows((rs) =>
      rs.map((r) =>
        r.coverageKey === coverageKey
          ? { ...r, accepted: !r.accepted, priceOverride: !r.accepted ? r.priceOverride : '' }
          : r
      )
    );
  };

  const setOverride = (coverageKey, value) => {
    setRows((rs) =>
      rs.map((r) => (r.coverageKey === coverageKey ? { ...r, priceOverride: value } : r))
    );
  };

  const save = async () => {
    // Validación: priceOverride (si está presente) debe ser un número >= 0.
    for (const r of rows) {
      if (r.accepted && r.priceOverride !== '') {
        const n = Number(r.priceOverride);
        if (Number.isNaN(n) || n < 0) {
          setError(t('professionalCoverages.priceInvalid'));
          return;
        }
      }
    }
    setSaving(true);
    setError('');
    const acceptedCoverages = rows
      .filter((r) => r.accepted)
      .map((r) => {
        const entry = { coverageKey: r.coverageKey };
        if (r.priceOverride !== '') entry.priceOverride = Number(r.priceOverride);
        return entry;
      });
    try {
      await updateProfessionalCoverages(professional.professionalId, acceptedCoverages);
      onClose();
    } catch (err) {
      console.error('Error saving professional coverages:', err);
      if (err?.response?.status === 403) {
        setError(t('professionalCoverages.forbidden'));
      } else {
        setError(t('professionalCoverages.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const coverageName = (key) => {
    const c = master.find((m) => m.key === key);
    return c ? c.name : key;
  };

  const isParticular = (key) => {
    const c = master.find((m) => m.key === key);
    return c ? !!c.isParticular : false;
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!saving) onClose();
      }}
    >
      <div className="modal-content modal-content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {t('professionalCoverages.forProfessional', {
              name: professional.name,
              label,
            })}
          </h2>
        </div>
        <div className="modal-body">
          <p className="prof-cov-subtitle">{t('professionalCoverages.subtitle', { label })}</p>
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : master.length === 0 ? (
            <div className="empty-state">{t('professionalCoverages.noMaster')}</div>
          ) : (
            <div className="table-responsive">
              <table className="data-table prof-cov-table">
                <thead>
                  <tr>
                    <th>{t('professionalCoverages.colAccepts')}</th>
                    <th>{t('professionalCoverages.colCoverage', { label })}</th>
                    <th>{t('professionalCoverages.colPriceOverride')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.coverageKey}>
                      <td>
                        <input
                          type="checkbox"
                          checked={r.accepted}
                          onChange={() => toggleAccept(r.coverageKey)}
                          aria-label={t('professionalCoverages.colAccepts')}
                        />
                      </td>
                      <td>
                        {coverageName(r.coverageKey)}
                        {isParticular(r.coverageKey) && (
                          <span className="list-tag prof-cov-particular">
                            {t('coverages.particular')}
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className="prof-cov-price"
                          value={r.priceOverride}
                          disabled={!r.accepted}
                          placeholder={t('professionalCoverages.inheritsDefault')}
                          onChange={(e) => setOverride(r.coverageKey, e.target.value)}
                          aria-label={t('professionalCoverages.colPriceOverride')}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={saving || loading}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CoveragesModal;
